import React, { useState, useMemo } from 'react';
import { useQuery } from 'react-query';
import axios from 'axios';
import {
  LayoutDashboard,
  Truck,
  Smartphone,
  MapPin,
  AlertTriangle,
  Map,
  Route,
  User,
  Users,
  BarChart3,
  LogOut,
  Navigation,
  Gauge,
  Activity,
  X,
  FileText,
  Building2,
  Bell,
  Search,
  ChevronDown,
  ChevronRight,
  Settings,
  Wrench,
  Fuel,
  Sparkles,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useSocket } from '../contexts/SocketContext';

const Sidebar = ({ collapsed, currentPath, onNavigate, isMobile, onCloseSidebar, open }) => {
  const { user, logout } = useAuth();
  const { systemAlerts } = useSocket();
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState({
    fleet: true,
    tracking: true,
    routes: true,
    management: true,
    system: true
  });

  // Fetch analytics notifications unread count
  const { data: unreadCountData } = useQuery(
    'analytics-notifications-unread-count',
    () => axios.get('/api/analytics/notifications/unread-count').then(res => res.data),
    {
      refetchInterval: 30000, // Refresh every 30 seconds
      enabled: user?.role === 'admin' || user?.role === 'manager' || user?.role === 'superadmin' // Only for managers/admins
    }
  );

  const analyticsUnreadCount = unreadCountData?.data?.unread_count || 0;

  // Групирај menu items по категории
  const menuItemsByCategory = {
    fleet: {
      label: 'Флот',
      icon: Truck,
      items: [
        {
          path: '/dashboard',
          label: 'Dashboard',
          icon: LayoutDashboard,
          roles: ['admin', 'manager', 'operator']
        },
        {
          path: '/installer-assistant',
          label: 'Технички Асистент',
          icon: Smartphone,
          roles: ['installer', 'superadmin']
        },
        {
          path: '/vehicles',
          label: 'Возила',
          icon: Truck,
          roles: ['admin', 'manager', 'operator']
        },
        {
          path: '/devices',
          label: 'GPS Уреди',
          icon: Smartphone,
          roles: ['admin', 'manager', 'operator']
        }
      ]
    },
    tracking: {
      label: 'Следење',
      icon: MapPin,
      items: [
        {
          path: '/device-monitor',
          label: 'Live Мониторинг',
          icon: Activity,
          roles: ['superadmin']
        },
        {
          path: '/map',
          label: 'Мапа',
          icon: Map,
          roles: ['admin', 'manager', 'operator']
        },
        {
          path: '/locations',
          label: 'Локации',
          icon: MapPin,
          roles: ['admin', 'manager', 'operator']
        },
        {
          path: '/alarms',
          label: 'Аларми',
          icon: AlertTriangle,
          roles: ['admin', 'manager', 'operator']
        }
      ]
    },
    routes: {
      label: 'Рути',
      icon: Route,
      items: [
        {
          path: '/routes',
          label: 'Рути',
          icon: Route,
          roles: ['admin', 'manager']
        },
        {
          path: '/route-export',
          label: 'Извозена рута',
          icon: Navigation,
          roles: ['admin', 'manager', 'operator']
        },
        {
          path: '/speed-dashboard',
          label: 'Брзина & Растојание',
          icon: Gauge,
          roles: ['admin', 'manager', 'operator']
        }
      ]
    },
    management: {
      label: 'Управување',
      icon: Users,
      items: [
        {
          path: '/geofences',
          label: 'Гео-ограничувања',
          icon: Map,
          roles: ['admin', 'manager']
        },
        {
          path: '/maintenance',
          label: 'Сервис',
          icon: Wrench,
          roles: ['admin', 'manager', 'operator']
        },
        {
          path: '/fuel-management',
          label: 'Гориво',
          icon: Fuel,
          roles: ['admin', 'manager', 'operator']
        },
        {
          path: '/drivers',
          label: 'Возачи',
          icon: User,
          roles: ['admin', 'manager', 'operator']
        },
        {
          path: '/analytics',
          label: 'Аналитики',
          icon: BarChart3,
          roles: ['admin', 'manager'],
          badge: analyticsUnreadCount
        },
        {
          path: '/users',
          label: 'Корисници',
          icon: Users,
          roles: ['admin', 'manager']
        },
        {
          path: '/tenants',
          label: 'Клиенти (Tenants)',
          icon: Building2,
          roles: ['superadmin']
        },
        {
          path: '/demo-accounts',
          label: 'Демо сметки',
          icon: Sparkles,
          roles: ['superadmin']
        },
        {
          path: '/reports',
          label: 'Извештаи',
          icon: BarChart3,
          roles: ['admin', 'manager', 'operator']
        }
      ]
    },
    system: {
      label: 'Систем',
      icon: Settings,
      items: [
        {
          path: '/settings/map',
          label: 'Поставки за Мапа',
          icon: Map,
          roles: ['admin', 'manager', 'operator']
        },
        {
          path: '/logs',
          label: 'Системски Логови',
          icon: FileText,
          roles: ['superadmin']
        },
        {
          path: '/alerts-dashboard',
          label: 'Системски Аларми (Health)',
          icon: Bell,
          roles: ['superadmin']
        }
      ]
    }
  };

  // Филтрирај и пребарај menu items
  const filteredMenuItems = useMemo(() => {
    const filtered = {};

    Object.entries(menuItemsByCategory).forEach(([categoryKey, category]) => {
      const categoryItems = category.items
        .filter(item => {
          // Superadmin gets the full app (all menu items); others are role-gated
          if (user?.role !== 'superadmin' && !item.roles.includes(user?.role)) return false;

          // Провери search query
          if (searchQuery) {
            const query = searchQuery.toLowerCase();
            return item.label.toLowerCase().includes(query) ||
              item.path.toLowerCase().includes(query);
          }

          return true;
        })
        .map(item => {
          // Ажурирај badge за System Alerts
          if (item.path === '/alerts-dashboard' && user?.role === 'superadmin' && systemAlerts) {
            const criticalCount = systemAlerts.filter(a =>
              a.severity === 'critical' || a.severity === 'high'
            ).length;
            return {
              ...item,
              badge: criticalCount > 0 ? criticalCount : null
            };
          }
          return item;
        });

      if (categoryItems.length > 0) {
        filtered[categoryKey] = {
          ...category,
          items: categoryItems
        };
      }
    });

    return filtered;
  // menuItemsByCategory is a static menu definition (stable content); intentionally omitted
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role, searchQuery, systemAlerts]);

  const handleLogout = () => {
    logout();
    onNavigate('/login');
    // Затвори sidebar на мобилен по logout
    if (isMobile && onCloseSidebar) {
      onCloseSidebar();
    }
  };

  const handleNavigation = (path) => {
    onNavigate(path);
    // Затвори sidebar на мобилен по клик
    if (isMobile && onCloseSidebar) {
      onCloseSidebar();
    }
  };

  const toggleCategory = (categoryKey) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryKey]: !prev[categoryKey]
    }));
  };

  return (
    <div className={`sidebar ${collapsed ? 'collapsed' : ''} ${open ? 'open' : ''}`}>
      {/* Header */}
      <div className="sidebar-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <img
              src={`${process.env.PUBLIC_URL || ''}/logo.svg`}
              alt="Ovatik Fleet"
              width={36}
              height={36}
              style={{ width: 36, height: 36, borderRadius: 10, flexShrink: 0 }}
            />
            <div style={{ minWidth: 0 }}>
              <h3 style={{
                margin: 0,
                fontSize: isMobile ? '1.1rem' : '1.2rem',
                fontWeight: 700,
                letterSpacing: '0.5px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}>
                Ovatik Fleet
              </h3>
              <small style={{
                fontSize: '0.75rem',
                opacity: 0.8,
                display: 'block',
                marginTop: '2px'
              }}>
                Fleet Management
              </small>
            </div>
          </div>
          {isMobile && onCloseSidebar && (
            <button
              onClick={onCloseSidebar}
              className="sidebar-close-btn"
              aria-label="Close menu"
              type="button"
            >
              <X size={22} />
            </button>
          )}
        </div>
      </div>

      {/* Search Bar (само ако не е collapsed) */}
      {!collapsed && (
        <div className="sidebar-search">
          <Search size={18} className="search-icon" />
          <input
            type="text"
            placeholder="Пребарај..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="sidebar-search-input"
            onClick={(e) => e.stopPropagation()}
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="sidebar-search-clear"
              aria-label="Clear search"
            >
              <X size={16} />
            </button>
          )}
        </div>
      )}

      {/* Navigation */}
      <nav className="sidebar-nav">
        {Object.entries(filteredMenuItems).map(([categoryKey, category]) => {
          const CategoryIcon = category.icon;
          const isExpanded = expandedCategories[categoryKey];

          return (
            <div key={categoryKey} className="sidebar-category">
              {/* Category Header */}
              {!collapsed && (
                <button
                  className="sidebar-category-header"
                  onClick={() => toggleCategory(categoryKey)}
                >
                  <CategoryIcon size={18} />
                  <span className="category-label">{category.label}</span>
                  {isExpanded ? (
                    <ChevronDown size={16} className="category-chevron" />
                  ) : (
                    <ChevronRight size={16} className="category-chevron" />
                  )}
                </button>
              )}

              {/* Category Items */}
              {isExpanded && (
                <div className="sidebar-category-items">
                  {category.items.map((item) => {
                    const Icon = item.icon;
                    const isActive = currentPath === item.path;

                    return (
                      <button
                        key={item.path}
                        className={`nav-item ${isActive ? 'active' : ''}`}
                        onClick={() => handleNavigation(item.path)}
                        title={collapsed ? item.label : undefined}
                      >
                        <Icon size={20} className="nav-icon" />
                        {!collapsed && (
                          <>
                            <span className="nav-label">{item.label}</span>
                            {item.badge !== null && item.badge !== undefined && item.badge > 0 && (
                              <span className="nav-badge">{item.badge}</span>
                            )}
                          </>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="sidebar-footer">
        <button
          className="nav-item footer-item"
          onClick={() => handleNavigation('/profile')}
          title={collapsed ? 'Профил' : undefined}
        >
          <User size={20} />
          {!collapsed && (
            <>
              <span>Профил</span>
              {user && (
                <span className="user-role-badge">{user.role}</span>
              )}
            </>
          )}
        </button>

        <button
          className="nav-item footer-item logout-btn"
          onClick={handleLogout}
          title={collapsed ? 'Одјави се' : undefined}
        >
          <LogOut size={20} />
          {!collapsed && 'Одјави се'}
        </button>
      </div>
    </div>
  );
};

export default Sidebar;













